/**
 * Footer actions for `CrmCreateForm`: the submit cluster (with the optional
 * "& add another" action, collapsed to a split button on narrow screens)
 * and the secondary slot (Cancel by default, or a config-supplied action).
 */

import { ChevronDown } from '@untitledui/icons';
import type { ReactNode } from 'react';
import type { Key } from 'react-aria-components';

import { Button } from '@/lib/base/buttons/button';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { Dropdown } from '@/lib/base/overlays/dropdown';

export interface CrmCreateFormFooterProps {
  submitLabel: string;
  /** True while `onSubmit` is in flight. */
  isSubmitting: boolean;
  /** Everything that must be loaded (schemas, association types) before a submit may start. */
  isReady: boolean;
  /** False when the user or the schema forbids creating this type. */
  isCreatable: boolean;
  /** Unacknowledged cardinality conflicts block submit and describe the button. */
  blockedByConflicts: boolean;
  showCreateAnother: boolean;
  /** `sm` breakpoint and up renders the two-button cluster; below it, a split button. */
  isDesktop: boolean;
  /** Narrow-screen split-button default: submit-and-add-another or plain submit. */
  preferCreateAnother: boolean;
  onToggleCreateDefault: (key: Key) => void;
  onSubmit: () => void;
  onSubmitAnother: () => void;
  onSubmitPreferred: () => void;
  onCancel: () => void;
  /** Replaces the Cancel button when provided. */
  secondaryAction: ReactNode;
  /** Capture-phase click on the submit cluster — marks fields touched so a
   *  disabled submit still reveals the errors blocking it. */
  onSubmitClusterClickCapture: () => void;
}

const MOBILE_LABEL = 'Create';

export function CrmCreateFormFooter({
  submitLabel,
  isSubmitting,
  isReady,
  isCreatable,
  blockedByConflicts,
  showCreateAnother,
  isDesktop,
  preferCreateAnother,
  onToggleCreateDefault,
  onSubmit,
  onSubmitAnother,
  onSubmitPreferred,
  onCancel,
  secondaryAction,
  onSubmitClusterClickCapture,
}: CrmCreateFormFooterProps) {
  const isDisabled = isSubmitting || !isReady || !isCreatable;
  const describedBy = blockedByConflicts ? 'conflict-panel-heading' : undefined;
  const busyLabel = 'Saving…';

  let mobileButtonLabel: string;
  if (isSubmitting) {
    mobileButtonLabel = busyLabel;
  } else if (preferCreateAnother) {
    mobileButtonLabel = `${MOBILE_LABEL} & add another`;
  } else {
    mobileButtonLabel = MOBILE_LABEL;
  }

  // Primary action leads, secondary sits beside it — the rhythm the
  // booking-link footer established. `flex-wrap` so a long submit label plus
  // Cancel wraps at a narrow panel width instead of overflowing the panel.
  return (
    <div className='flex flex-wrap items-center gap-2.5'>
      <div
        className='flex items-center gap-2.5'
        onClickCapture={onSubmitClusterClickCapture}
      >
        {isDesktop && showCreateAnother && (
          <>
            <SubmitButton
              color='secondary'
              size='md'
              onClick={onSubmit}
              isDisabled={isDisabled}
              isLoading={isSubmitting}
              aria-describedby={describedBy}
              data-testid='crm-create-form-submit'
            >
              {isSubmitting ? busyLabel : submitLabel}
            </SubmitButton>
            <SubmitButton
              color='primary'
              size='md'
              onClick={onSubmitAnother}
              isDisabled={isDisabled}
              isLoading={isSubmitting}
              aria-describedby={describedBy}
            >
              {isSubmitting ? busyLabel : `${submitLabel} & add another`}
            </SubmitButton>
          </>
        )}
        {isDesktop && !showCreateAnother && (
          <SubmitButton
            color='primary'
            size='md'
            onClick={onSubmit}
            isDisabled={isDisabled}
            isLoading={isSubmitting}
            aria-describedby={describedBy}
            data-testid='crm-create-form-submit'
          >
            {isSubmitting ? busyLabel : submitLabel}
          </SubmitButton>
        )}
        {!isDesktop && showCreateAnother && (
          <div className='flex items-center -space-x-px'>
            <SubmitButton
              color='primary'
              size='md'
              onClick={onSubmitPreferred}
              isDisabled={isDisabled}
              isLoading={isSubmitting}
              aria-describedby={describedBy}
              className='!rounded-r-none before:!rounded-r-none'
              data-testid='crm-create-form-submit'
            >
              {mobileButtonLabel}
            </SubmitButton>
            <Dropdown.Root>
              <Button
                type='button'
                color='primary'
                size='md'
                iconLeading={ChevronDown}
                isDisabled={isSubmitting || !isReady || blockedByConflicts}
                className='!rounded-l-none before:!rounded-l-none border-l border-white/20'
                aria-label='Change default create action'
              />
              <Dropdown.Popover placement='top end'>
                <Dropdown.Menu onAction={onToggleCreateDefault}>
                  <Dropdown.Item
                    id='create'
                    textValue={MOBILE_LABEL}
                    label={MOBILE_LABEL}
                  />
                  <Dropdown.Item
                    id='another'
                    textValue={`${MOBILE_LABEL} & add another`}
                    label={`${MOBILE_LABEL} & add another`}
                  />
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </div>
        )}
        {!isDesktop && !showCreateAnother && (
          <SubmitButton
            color='primary'
            size='md'
            onClick={onSubmit}
            isDisabled={isDisabled}
            isLoading={isSubmitting}
            aria-describedby={describedBy}
            data-testid='crm-create-form-submit'
          >
            {isSubmitting ? busyLabel : MOBILE_LABEL}
          </SubmitButton>
        )}
      </div>
      {secondaryAction ?? (
        <Button
          type='button'
          color='secondary'
          size='md'
          onClick={onCancel}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
      )}
    </div>
  );
}
